import { useEffect, useState } from "react";
import "@/App.css";
import { Toaster } from 'sonner';
import SharedKundliPage from './pages/SharedKundliPage';
import RemindersPage from './pages/RemindersPage';
import { BACKEND_URL } from './lib/backend';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";
import { PWAProvider, usePWA } from "./context/PWAContext";
import { Loader2 } from "lucide-react";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ExitIntentPopup from "./components/ExitIntentPopup";

// Pages - Website
import LandingPage from "./pages/ShubhLandingPage";
import ShubhLoginPage from "./pages/ShubhLoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import ProfilesPage from "./pages/ProfilesPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import KundliMilanPage from "./pages/KundliMilanPage";
import JanamKundliPage from "./pages/JanamKundliPage";
import AdminDashboard from "./pages/AdminDashboard";
import AuthCallback from "./pages/AuthCallback";
import YearlyPredictionsPage from "./pages/YearlyPredictionsPage";
import FamilyCompatibilityPage from "./pages/FamilyCompatibilityPage";
import PalmReadingPage from "./pages/PalmReadingPage";
import HelpPage from "./pages/HelpPage";
import HinduCalendarPage from "./pages/HinduCalendarPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import VedicGuruPage from "./pages/VedicGuruPage";

// Pages - App Mode
import AppDashboard from "./pages/AppDashboard";
import withAppLayout from "./components/withAppLayout";

// Shubh 2.0 Design System Components
import { 
  ShubhDashboard, 
  ShubhExplorePage, 
  ShubhTimelinePage,
  ShubhRashifalPage,
  ShubhZodiacDetailPage,
  ShubhEventDetailPage,
  ShubhKundliPage,
  ShubhPlanetaryPositionsPage,
  ShubhDashaTimelinePage,
  ShubhProfilesPage
} from "./components/shubh";

// Feature flag for new design (can be toggled)
const USE_SHUBH_DESIGN = true;

// Wrap pages for app mode
const AppProfilesPage = withAppLayout(ProfilesPage, "Profile");
const AppKundliMilanPage = withAppLayout(KundliMilanPage, "Kundli Match");
const AppJanamKundliPage = withAppLayout(JanamKundliPage, "Janam Kundli");
const AppSubscriptionPage = withAppLayout(SubscriptionPage, "Subscription");
const AppYearlyPredictionsPage = withAppLayout(YearlyPredictionsPage, "Yearly Forecast");
const AppFamilyCompatibilityPage = withAppLayout(FamilyCompatibilityPage, "Family Compatibility");
const AppPalmReadingPage = withAppLayout(PalmReadingPage, "Palm Reading");
const AppHinduCalendarPage = withAppLayout(HinduCalendarPage, "Hindu Panchang");

// Register Service Worker for PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('Nakshatra PWA: Service Worker registered', registration.scope);
      })
      .catch((error) => {
        console.log('Nakshatra PWA: Service Worker registration failed', error);
      });
  });
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [forceShow, setForceShow] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    // If user data passed from AuthCallback, we're authenticated
    if (location.state?.user) {
      return;
    }
  }, [location.state]);

  // Fallback: If loading takes more than 5 seconds, force redirect to login
  // This prevents infinite spinner in PWA edge cases
  useEffect(() => {
    if (loading) {
      console.log('Nakshatra: Auth loading started');
      const timeout = setTimeout(() => {
        console.log('Nakshatra: Auth loading timeout, forcing redirect');
        setForceShow(true);
      }, 5000);
      
      // Show help text after 3 seconds
      const helpTimeout = setTimeout(() => {
        setShowHelp(true);
      }, 3000);
      
      return () => {
        clearTimeout(timeout);
        clearTimeout(helpTimeout);
      };
    }
  }, [loading]);

  if (loading && !forceShow) {
    return (
      <div className="min-h-screen bg-[#FFF9EF] flex flex-col items-center justify-center" data-testid="auth-loading">
        <div className="w-12 h-12 border-4 border-[#FCE9D9] border-t-[#C84B32] rounded-full animate-spin"></div>
        <p className="text-[#C84B32] mt-4 text-sm font-medium">Checking login...</p>
        {showHelp && (
          <p className="text-[#8A8279] mt-2 text-xs text-center px-8">
            Taking longer than expected...<br/>
            <a href="/login" className="text-[#C84B32] underline mt-2 inline-block">Go to Login</a>
          </p>
        )}
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router Component
const trackVisit = (page) => {
  // A share token is a private capability; never send it to analytics.
  if (page.startsWith('/shared/')) return;
  let visitorId = localStorage.getItem('nakshatra_visitor_id');
  if (!visitorId) {
    visitorId = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem('nakshatra_visitor_id', visitorId);
  }
  fetch(`${BACKEND_URL}/api/track/visit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ page, visitor_id: visitorId })
  }).catch(() => {});
};

const AppRouter = () => {
  const location = useLocation();
  const { isStandalone } = usePWA();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackVisit(location.pathname);
  }, [location.pathname]);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions with ProtectedRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Use Shubh 2.0 design when feature flag is enabled
  const DashboardComponent = USE_SHUBH_DESIGN ? ShubhDashboard : (isStandalone ? AppDashboard : Dashboard);
  const ProfilesComponent = USE_SHUBH_DESIGN ? ShubhProfilesPage : (isStandalone ? AppProfilesPage : ProfilesPage);
  const legacy = (page, appPage) => (USE_SHUBH_DESIGN || !isStandalone ? page : appPage);
  const KundliMilanComponent = legacy(KundliMilanPage, AppKundliMilanPage);
  const JanamKundliComponent = USE_SHUBH_DESIGN ? ShubhKundliPage : (isStandalone ? AppJanamKundliPage : JanamKundliPage);
  const SubscriptionComponent = legacy(SubscriptionPage, AppSubscriptionPage);
  const YearlyPredictionsComponent = legacy(YearlyPredictionsPage, AppYearlyPredictionsPage);
  const FamilyCompatibilityComponent = legacy(FamilyCompatibilityPage, AppFamilyCompatibilityPage);
  const PalmReadingComponent = legacy(PalmReadingPage, AppPalmReadingPage);
  const HinduCalendarComponent = legacy(HinduCalendarPage, AppHinduCalendarPage);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/shared/kundli/:token" element={<SharedKundliPage />} />
      <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
      
      {/* In standalone mode, go directly to dashboard if logged in */}
      <Route path="/" element={isStandalone ? <Navigate to="/dashboard" replace /> : <LandingPage />} />
      <Route path="/login" element={<ShubhLoginPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardComponent />
          </ProtectedRoute>
        } 
      />
      {/* New Shubh 2.0 Routes */}
      <Route 
        path="/explore" 
        element={
          <ProtectedRoute>
            <ShubhExplorePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/timeline" 
        element={
          <ProtectedRoute>
            <ShubhTimelinePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/timeline/:eventId" 
        element={
          <ProtectedRoute>
            <ShubhEventDetailPage />
          </ProtectedRoute>
        } 
      />
      {/* Rashifal Routes */}
      <Route 
        path="/rashifal" 
        element={
          <ProtectedRoute>
            <ShubhRashifalPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/rashifal/:signId" 
        element={
          <ProtectedRoute>
            <ShubhZodiacDetailPage />
          </ProtectedRoute>
        } 
      />
      {/* Kundli Routes */}
      <Route 
        path="/kundli/planets" 
        element={
          <ProtectedRoute>
            <ShubhPlanetaryPositionsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/kundli/dasha" 
        element={
          <ProtectedRoute>
            <ShubhDashaTimelinePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/profiles" 
        element={
          <ProtectedRoute>
            <ProfilesComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/subscription" 
        element={
          <ProtectedRoute>
            <SubscriptionComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/kundli-milan" 
        element={
          <ProtectedRoute>
            <KundliMilanComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/janam-kundli" 
        element={
          <ProtectedRoute>
            <JanamKundliComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/admin" 
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/yearly-forecast" 
        element={
          <ProtectedRoute>
            <YearlyPredictionsComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/family-compatibility" 
        element={
          <ProtectedRoute>
            <FamilyCompatibilityComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/palm-reading" 
        element={
          <ProtectedRoute>
            <PalmReadingComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/hindu-calendar" 
        element={
          <ProtectedRoute>
            <HinduCalendarComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/help" 
        element={
          <ProtectedRoute>
            <HelpPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/vedic-guru" 
        element={
          <ProtectedRoute>
            <VedicGuruPage />
          </ProtectedRoute>
        } 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App shubh-app">
      <BrowserRouter>
        <PWAProvider>
          <ThemeProvider>
            <LanguageProvider>
              <AuthProvider>
                <AppRouter />
                <Toaster position="top-center" richColors />
                <PWAInstallPrompt />
                <ExitIntentPopup />
              </AuthProvider>
            </LanguageProvider>
          </ThemeProvider>
        </PWAProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
